import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

export default function ExpandableNotice({ content, html = false, previewLength, consumeOutsideClick = false, deferResize = false, collapsedLines = 3 }: {
  content: string;
  html?: boolean;
  previewLength?: number;
  consumeOutsideClick?: boolean;
  deferResize?: boolean;
  collapsedLines?: number;
}) {
  const contentId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [keepContentWidth, setKeepContentWidth] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const compact = previewLength !== undefined;
  const preview = useMemo(() => {
    if (previewLength === undefined) return null;
    const body = html ? new DOMParser().parseFromString(content, 'text/html').body : null;
    body?.querySelectorAll('br, p, div, li').forEach(element => element.after(' '));
    const text = (body?.textContent ?? content).replace(/\s+/g, ' ').trim();
    const parts = Array.from(text);
    const hasImage = Boolean(body?.querySelector('img'));
    return {
      text: parts.slice(0, previewLength).join('') || (hasImage ? '이미지 공지' : ''),
      canExpand: parts.length > previewLength || hasImage,
    };
  }, [content, html, previewLength]);
  const canExpand = Boolean(preview?.canExpand || overflows);

  useEffect(() => setExpanded(false), [content]);

  useLayoutEffect(() => {
    if (expanded) {
      setKeepContentWidth(true);
      return;
    }
    // Finish folding vertically before shrinking the hidden content's width.
    const timer = window.setTimeout(() => setKeepContentWidth(false), 260);
    return () => window.clearTimeout(timer);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    const collapseOutside = (event: Event) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        if (consumeOutsideClick) {
          event.preventDefault();
          event.stopPropagation();
        }
        setExpanded(false);
      }
    };
    const outsideEvent = consumeOutsideClick ? 'click' : 'pointerdown';
    document.addEventListener(outsideEvent, collapseOutside, true);
    if (!consumeOutsideClick) document.addEventListener('focusin', collapseOutside, true);
    return () => {
      document.removeEventListener(outsideEvent, collapseOutside, true);
      document.removeEventListener('focusin', collapseOutside, true);
    };
  }, [expanded, consumeOutsideClick]);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) return;
    const measure = () => {
      const lineHeight = parseFloat((element.ownerDocument.defaultView ?? window).getComputedStyle(element).lineHeight);
      setOverflows(compact ? element.scrollWidth > element.clientWidth + 1 : element.scrollHeight > lineHeight * collapsedLines + 1);
    };
    measure();
    let frame: { view: Window; id: number } | undefined;
    const scheduleMeasure = () => {
      if (frame) frame.view.cancelAnimationFrame(frame.id);
      const view = element.ownerDocument.defaultView ?? window;
      frame = { view, id: view.requestAnimationFrame(() => { frame = undefined; measure(); }) };
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(deferResize ? scheduleMeasure : measure);
    observer?.observe(element);
    return () => { observer?.disconnect(); if (frame) frame.view.cancelAnimationFrame(frame.id); };
  }, [content, compact, previewLength, deferResize, collapsedLines]);

  const toggle = canExpand && <Toggle type="button" $compact={compact} aria-expanded={expanded} aria-controls={contentId}
      aria-label={compact ? (expanded ? '공지 접기' : '공지 펼치기') : undefined}
      onClick={event => { event.stopPropagation(); setExpanded(value => !value); }}>
      {compact ? <Chevron viewBox="0 0 20 20" fill="none" aria-hidden="true" $expanded={expanded}>
        <path d="m5 7.5 5 5 5-5" />
      </Chevron> : expanded ? '접기' : '펼쳐보기'}
    </Toggle>;

  return <Container ref={containerRef} $compact={compact}>
    {preview ? <>
      <Preview ref={contentRef} $expanded={expanded} aria-hidden={expanded}>{preview.text}{preview.canExpand ? '...' : ''}</Preview>
      {canExpand && <Expansion id={contentId} $expanded={expanded} $keepWidth={expanded || keepContentWidth} aria-hidden={!expanded} inert={!expanded}>
        <Clip>
          <div role="region" aria-label="공지 전체 내용">
            <Content $expanded={true}
              {...(html ? { dangerouslySetInnerHTML: { __html: content } } : { children: content })} />
          </div>
        </Clip>
      </Expansion>}
      {canExpand && <Footer $expanded={expanded}>{toggle}</Footer>}
    </> : <>
      <Content id={contentId} ref={contentRef} $expanded={expanded} $collapsedLines={collapsedLines}
        {...(html ? { dangerouslySetInnerHTML: { __html: content } } : { children: content })} />
      {toggle}
    </>}
  </Container>;
}

const Container = styled.div<{ $compact: boolean }>`
  min-width: 0;
  width: 100%;
  ${({ $compact }) => $compact && 'display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 8px; align-items: center;'}
`;

const Preview = styled.div<{ $expanded: boolean }>`
  min-width: 0;
  grid-column: 1;
  grid-row: 1;
  max-height: ${({ $expanded }) => $expanded ? '0' : '1.5em'};
  opacity: ${({ $expanded }) => $expanded ? 0 : 1};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.5;
  transition: max-height 240ms ease, opacity 160ms ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Expansion = styled.div<{ $expanded: boolean; $keepWidth: boolean }>`
  grid-column: 1 / -1;
  grid-row: 2;
  min-width: 0;
  width: ${({ $keepWidth }) => $keepWidth ? 'auto' : '0'};
  display: grid;
  grid-template-rows: ${({ $expanded }) => $expanded ? '1fr' : '0fr'};
  opacity: ${({ $expanded }) => $expanded ? 1 : 0};
  transition: grid-template-rows 240ms ease, opacity 200ms ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Clip = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const Footer = styled.div<{ $expanded: boolean }>`
  grid-column: 2;
  grid-row: ${({ $expanded }) => $expanded ? 3 : 1};
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ $expanded }) => $expanded ? '4px' : '0'};
`;

const Content = styled.div<{ $expanded: boolean; $collapsedLines?: number }>`
  min-width: 0;
  flex: 1;
  display: ${({ $expanded }) => $expanded ? 'block' : '-webkit-box'};
  -webkit-line-clamp: ${({ $collapsedLines = 3 }) => $collapsedLines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: keep-all;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const Toggle = styled.button<{ $compact: boolean }>`
  position: relative;
  display: block;
  flex-shrink: 0;
  min-height: ${({ $compact }) => $compact ? '20px' : '44px'};
  padding: ${({ $compact }) => $compact ? '0' : '6px 0'};
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: calc(13px * var(--studio-font-scale, 1));
  text-decoration: ${({ $compact }) => $compact ? 'none' : 'underline'};
  text-underline-offset: 3px;
  cursor: pointer;
  ${({ $compact }) => $compact && `&::before { content: ''; position: absolute; inset: -12px; }`}
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; border-radius: 4px; }
`;

const Chevron = styled.svg<{ $expanded: boolean }>`
  display: block;
  width: 20px;
  height: 20px;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
  transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'none'};
  transition: transform 240ms ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
